/**
 * Routes over a scene's occupancy grid (lifted from the shared prototype
 * engine's `navigation.js`, `findPath`): A* over the `walk.cell` lattice in
 * eight directions, never cutting diagonally past a blocked corner, costs 1
 * and √2, the straight-line distance as the heuristic, at most
 * `walk.searchLimit` expansions. The route is the lattice nodes after the
 * start, ending at the exact goal; empty when the goal's node cannot be
 * reached. The prototype scanned its open list for the lowest `f`; this keeps
 * a binary heap ordered by `f` and then by insertion, which picks the same
 * node (the earliest pushed of the lowest `f`), only faster.
 */
import type { Point, WalkMap } from './map.ts';

type Node = { readonly i: number; readonly j: number; readonly g: number; readonly f: number; readonly seq: number; readonly parent: Node | null };

/** The prototype's order, in its (x, z) axes: east, west, south, north, then the diagonals (z is -y here). */
const directions: readonly (readonly [number, number])[] = [
  [1, 0],
  [-1, 0],
  [0, -1],
  [0, 1],
  [1, -1],
  [1, 1],
  [-1, -1],
  [-1, 1],
];

const before = (a: Node, b: Node): boolean => a.f < b.f || (a.f === b.f && a.seq < b.seq);

class Heap {
  private readonly items: Node[] = [];
  get size(): number {
    return this.items.length;
  }
  push(n: Node): void {
    const a = this.items;
    a.push(n);
    let k = a.length - 1;
    while (k > 0) {
      const up = (k - 1) >> 1;
      const p = a[up];
      if (p === undefined || !before(n, p)) break;
      a[k] = p;
      k = up;
    }
    a[k] = n;
  }
  pop(): Node | undefined {
    const a = this.items;
    const top = a[0];
    const last = a.pop();
    if (top === undefined || last === undefined || a.length === 0) return top;
    let k = 0;
    for (;;) {
      const l = 2 * k + 1;
      const r = l + 1;
      let m = k;
      let best = last;
      const left = a[l];
      const right = a[r];
      if (left !== undefined && before(left, best)) {
        m = l;
        best = left;
      }
      if (right !== undefined && before(right, best)) {
        m = r;
        best = right;
      }
      if (m === k) break;
      a[k] = best;
      k = m;
    }
    a[k] = last;
    return top;
  }
}

/** The route from `from` to `to` over the map's lattice; [] when there is none. */
export const findPath = (map: WalkMap, from: Point, to: Point): Point[] => {
  const cell = map.cell;
  const snap = (v: number): number => Math.round(v / cell);
  const si = snap(from.x);
  const sj = snap(from.y);
  const gi = snap(to.x);
  const gj = snap(to.y);
  const key = (i: number, j: number): number => (j - map.j0 + 1) * (map.columns + 2) + (i - map.i0 + 1);
  const best = new Map<number, number>([[key(si, sj), 0]]);
  const open = new Heap();
  let seq = 0;
  open.push({ i: si, j: sj, g: 0, f: 0, seq, parent: null });
  for (let count = 0; open.size > 0 && count < map.scene.walk.searchLimit; count += 1) {
    const node = open.pop();
    if (node === undefined) break;
    if (node.i === gi && node.j === gj) {
      const path: Point[] = [{ x: to.x, y: to.y }];
      for (let p: Node | null = node; p?.parent; p = p.parent) path.push({ x: p.i * cell, y: p.j * cell });
      return path.reverse();
    }
    // A node reached again more cheaply was pushed again; this copy is stale.
    if ((best.get(key(node.i, node.j)) ?? Infinity) < node.g) continue;
    for (const [di, dj] of directions) {
      const i = node.i + di;
      const j = node.j + dj;
      if (!map.node(i, j)) continue;
      if (di !== 0 && dj !== 0 && (!map.node(node.i, j) || !map.node(i, node.j))) continue;
      const g = node.g + Math.hypot(di, dj);
      const k = key(i, j);
      const known = best.get(k);
      if (known !== undefined && known <= g) continue;
      best.set(k, g);
      seq += 1;
      open.push({ i, j, g, f: g + Math.hypot(gi - i, gj - j), seq, parent: node });
    }
  }
  return [];
};
